import {
  ArrayInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanInput,
  Create,
  DateTimeInput,
  FormDataConsumer,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  SelectInput,
  SimpleFormIterator,
  useSimpleFormIteratorItem,
  TextInput,
} from "react-admin";
import { TabbedForm } from "../common/TabbedForm";
import { Box } from "@mui/material";
import {
  validateDiscountCouponFunding,
  validatePercentage,
  validateRequired,
} from "../utils/Validations";
import Spacer from "../common/Spacer";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { PRODUCT_STATUS } from "../enums/ProductTypes";
import { ROLE_TYPES } from "../enums/Roles";
import {
  DISCOUNT_COUPON_APPLIES_TO_CHOICES,
  DISCOUNT_COUPON_DISCOUNT_TYPE_CHOICES,
  DISCOUNT_COUPON_FUNDING_CHOICES,
  DISCOUNT_COUPON_REDEMPTION_CHOICES,
  DISCOUNT_COUPON_STOCK_TYPE,
  DISCOUNT_COUPON_TYPE_CHOICES,
  isMorfyFund,
  isRestaurantFund,
} from "../utils/DiscountCouponUtil";
import { DISCOUNT_COUPON_DISCOUNT_TYPE } from "../enums/DiscountCouponDiscountType";
import { DISCOUNT_COUPON_APPLIES_TO } from "../enums/DiscountCouponAppliesToType";
import { STOCK_TYPES } from "../enums/StockTypes";
import { DISCOUNT_COUPON_TYPE } from "../enums/DiscountCouponType";
import { COUNTRIES_SHORT } from "../enums/Countries";
import { useWatch } from "react-hook-form";
import AbsoluteSource from "../common/AbsoluteSource";

const FundingInput = ({ discountType, source, label, ...rest }) => {
  if (discountType === DISCOUNT_COUPON_DISCOUNT_TYPE.PERCENTAGE) {
    return (
      <NumberInput
        source={source}
        label={label}
        fullWidth
        min={0}
        max={100}
        format={(v) => (v ? (v * 100).toFixed() : "0")}
        parse={(v) => v && parseFloat(v) / 100}
        validate={[
          ...validatePercentage,
          validateDiscountCouponFunding,
          validateRequired,
        ]}
        onWheel={(e) => e.target.blur()}
      />
    );
  }

  return (
    <NumberInput
      source={source}
      label={label}
      fullWidth
      {...rest}
      validate={[validateDiscountCouponFunding, validateRequired]}
    />
  );
};

const CouponProductRow = () => {
  const { index } = useSimpleFormIteratorItem();
  const restaurantProduct = useWatch({ name: "appliesTo.restaurantProduct" });
  return (
    <Box sx={{ flex: "1" }}>
      <Box sx={{ display: "flex" }}>
        <AbsoluteSource>
          <ReferenceInput
            source={`appliesTo.restaurantProduct[${index}]`}
            reference="restaurants"
            perPage={100}
            filter={{
              active: true,
              type: RESTAURANT_TYPES.REGULAR,
            }}
            fullWidth
          >
            <AutocompleteInput
              filterToQuery={(searchText) => ({
                name: searchText,
              })}
              label="Restaurante"
              optionText="name"
              fullWidth
            />
          </ReferenceInput>
        </AbsoluteSource>

        <Spacer />
        <ReferenceInput
          // Empty source = the array's scalar element (products.<i>)
          source=""
          reference="products"
          perPage={10000}
          sort={{ field: "name", order: "ASC" }}
          filter={{
            restaurant: restaurantProduct?.[index],
            status: [PRODUCT_STATUS.ACTIVE],
          }}
          fullWidth
        >
          <SelectInput
            optionText="name"
            label="Producto"
            readOnly={!restaurantProduct?.[index]}
            validate={validateRequired}
            fullWidth
          />
        </ReferenceInput>
      </Box>
    </Box>
  );
};

const DiscountCouponEdit = () => {
  const transform = (data) => {
    return {
      ...data,
      discount: {
        ...data?.discount,
        maxDiscountLimit: data?.discount?.maxDiscountLimit || undefined,
        minPurchaseAmount: data?.discount?.minPurchaseAmount || undefined,
      },
    };
  };

  return (
    <Create
      title="Crear Cupón"
      mutationMode="pessimistic"
      redirect="list"
      transform={transform}
    >
      <TabbedForm>
        <TabbedForm.Tab label="Datos básicos">
          <FormDataConsumer>
            {({ formData, ...rest }) => (
              <Box>
                <Box sx={{ display: "flex" }}>
                  <Box sx={{ flex: 0.5, mr: "2em" }}>
                    <TextInput
                      source="title"
                      label="Titulo"
                      fullWidth
                      validate={validateRequired}
                    />
                    <TextInput
                      source="description"
                      label="Descripción"
                      validate={validateRequired}
                      fullWidth
                    />
                    <TextInput
                      source="code"
                      label="Código"
                      fullWidth
                      validate={validateRequired}
                      format={(v) => v?.toUpperCase() || ""}
                      parse={(v) => v?.toUpperCase() || ""}
                    />
                    <BooleanInput source="active" label="Activo" fullWidth />
                  </Box>
                  <Box sx={{ flex: 0.5 }}>
                    <DateTimeInput
                      source="expirationDate"
                      label="Fecha de expiración"
                      parse={(v) => v && new Date(v).toISOString()}
                      fullWidth
                    />
                    <SelectInput
                      source="redemptionCondition"
                      label="Condiciones de canje"
                      choices={DISCOUNT_COUPON_REDEMPTION_CHOICES}
                      validate={validateRequired}
                      fullWidth
                    />
                    <Box sx={{ display: "flex" }}>
                      <Box sx={{ flex: 0.5, mr: "2em" }}>
                        <SelectInput
                          source="type"
                          label="Audiencia"
                          choices={DISCOUNT_COUPON_TYPE_CHOICES}
                          validate={validateRequired}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: 0.5 }}>
                        {formData.type === DISCOUNT_COUPON_TYPE.TARGETED && (
                          <ReferenceInput
                            source="user"
                            reference="users"
                            filter={{
                              active: true,
                              role: ROLE_TYPES.CUSTOMER,
                            }}
                            label="Usuario"
                            alwaysOn
                          >
                            <AutocompleteInput
                              filterToQuery={(searchText) => ({
                                email: searchText,
                              })}
                              label="Usuario"
                              optionText="email"
                              validate={validateRequired}
                            />
                          </ReferenceInput>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </FormDataConsumer>
        </TabbedForm.Tab>
        <TabbedForm.Tab label="Descuento">
          <FormDataConsumer>
            {({ formData, ...rest }) => (
              <Box>
                <Box sx={{ display: "flex" }}>
                  <Box sx={{ flex: 0.5, mr: "2em" }}>
                    <SelectInput
                      source="funding.fundedBy"
                      label="Pagado por"
                      choices={DISCOUNT_COUPON_FUNDING_CHOICES}
                      validate={validateRequired}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ flex: 0.5 }}>
                    {isRestaurantFund(formData?.funding?.fundedBy) && (
                      <FundingInput
                        source="funding.restaurantFund"
                        label="Descuento Restaurante"
                        discountType={formData?.discount?.type}
                      />
                    )}
                    {isMorfyFund(formData?.funding?.fundedBy) && (
                      <FundingInput
                        source="funding.morfyFund"
                        label="Descuento Morfy"
                        discountType={formData?.discount?.type}
                      />
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex" }}>
                  <Box sx={{ flex: 0.5, mr: "2em" }}>
                    <SelectInput
                      source="discount.type"
                      label="Tipo de descuento"
                      choices={DISCOUNT_COUPON_DISCOUNT_TYPE_CHOICES}
                      validate={validateRequired}
                      fullWidth
                    />
                    <NumberInput
                      source="discount.maxDiscountLimit"
                      label="Tope de descuento"
                      parse={(v) => v && parseFloat(v)}
                      fullWidth
                    />
                    <NumberInput
                      source="discount.minPurchaseAmount"
                      label="Mínimo de compra"
                      parse={(v) => v && parseFloat(v)}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ flex: 0.5 }}>
                    {formData?.discount?.type ===
                    DISCOUNT_COUPON_DISCOUNT_TYPE.PERCENTAGE ? (
                      <NumberInput
                        source="discount.percentageValue"
                        label="Valor del descuento (%)"
                        fullWidth
                        min={0}
                        max={100}
                        format={(v) => (v ? (v * 100).toFixed() : "0")}
                        parse={(v) => v && parseFloat(v) / 100}
                        validate={validatePercentage}
                        onWheel={(e) => e.target.blur()}
                      />
                    ) : formData?.discount?.type ===
                      DISCOUNT_COUPON_DISCOUNT_TYPE.AMOUNT ? (
                      <NumberInput
                        source="discount.amountValue"
                        label="Valor del descuento ($)"
                        fullWidth
                        min={0}
                        validate={validateRequired}
                        onWheel={(e) => e.target.blur()}
                      />
                    ) : null}
                  </Box>
                </Box>
              </Box>
            )}
          </FormDataConsumer>
        </TabbedForm.Tab>
        <TabbedForm.Tab label="Aplica a">
          <FormDataConsumer>
            {({ formData, ...rest }) => (
              <>
                <Box sx={{ display: "flex" }}>
                  <Box sx={{ flex: 0.5, mr: "2em" }}>
                    <SelectInput
                      source="appliesTo.type"
                      label="Aplica a"
                      choices={DISCOUNT_COUPON_APPLIES_TO_CHOICES}
                      validate={validateRequired}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ flex: 0.5 }}>
                    {formData?.appliesTo?.type ===
                      DISCOUNT_COUPON_APPLIES_TO.PRODUCT && (
                      <ArrayInput source="appliesTo.products" label="">
                        <SimpleFormIterator disableReordering>
                          <CouponProductRow />
                        </SimpleFormIterator>
                      </ArrayInput>
                    )}
                    {formData?.appliesTo?.type ===
                      DISCOUNT_COUPON_APPLIES_TO.RESTAURANT && (
                      <>
                        <ReferenceArrayInput
                          source="appliesTo.restaurants"
                          reference="restaurants"
                          perPage={100}
                          filter={{
                            active: true,
                            type: RESTAURANT_TYPES.REGULAR,
                          }}
                          sort={{ field: "name", order: "ASC" }}
                        >
                          <AutocompleteArrayInput
                            filterToQuery={(searchText) => ({
                              name: searchText,
                            })}
                            label="Restaurante(s)"
                            helperText="Puedes elegir más de un restaurante"
                            fullWidth
                            optionText="name"
                          />
                        </ReferenceArrayInput>
                      </>
                    )}
                    {formData?.appliesTo?.type ===
                      DISCOUNT_COUPON_APPLIES_TO.PLACE_TYPE && (
                      <>
                        <ReferenceArrayInput
                          source="appliesTo.placeTypes"
                          reference="place-types"
                          perPage={100}
                          filter={{
                            active: true,
                          }}
                          sort={{ field: "name", order: "ASC" }}
                        >
                          <AutocompleteArrayInput
                            filterToQuery={(searchText) => ({
                              name: searchText,
                            })}
                            label="Tipo de lugar(es)"
                            helperText="Puedes elegir más de un restaurante"
                            fullWidth
                            optionText="name"
                          />
                        </ReferenceArrayInput>
                      </>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex" }}>
                  <ReferenceArrayInput
                    source="appliesTo.regions"
                    reference="regions"
                    perPage={100}
                    filter={{
                      active: true,
                      country: [COUNTRIES_SHORT.CL],
                    }}
                    sort={{ field: "name", order: "ASC" }}
                  >
                    <AutocompleteArrayInput
                      filterToQuery={(searchText) => ({
                        name: searchText,
                      })}
                      label="Regiones"
                      helperText="Puedes elegir más de una región"
                      fullWidth
                      optionText="name"
                    />
                  </ReferenceArrayInput>
                </Box>
              </>
            )}
          </FormDataConsumer>
        </TabbedForm.Tab>
        <TabbedForm.Tab label="Usos">
          <FormDataConsumer>
            {({ formData, ...rest }) => (
              <Box sx={{ display: "flex" }}>
                <Box sx={{ flex: 0.5, mr: "2em" }}>
                  <SelectInput
                    source="stockType"
                    label="Tipo de Uso"
                    choices={DISCOUNT_COUPON_STOCK_TYPE}
                    validate={validateRequired}
                    fullWidth
                  />
                </Box>
                <Box sx={{ flex: 0.5, mr: "2em" }}>
                  {formData?.stockType === STOCK_TYPES.LIMITED && (
                    <TextInput
                      source="stock"
                      label="Máximo de usos"
                      fullWidth
                    />
                  )}
                </Box>
              </Box>
            )}
          </FormDataConsumer>
        </TabbedForm.Tab>
      </TabbedForm>
    </Create>
  );
};

export default DiscountCouponEdit;
